import { Record, User } from './general'

// ..\mowazaf\Data\Employee\Entities\UserEducation.cs
export interface UserEducation extends Record {
    id: number;
    userId: string;
    user?: User;
    institutionName: string;
    degree: string;
    fieldOfStudy?: string;
    startDate?: Date;
    endDate?: Date;
    grade?: string;
}

// ..\mowazaf\Data\Employee\Entities\UserWorkExperience.cs
export interface UserWorkExperience extends Record {
    id: number;
    userId: string;
    user?: User;
    companyName: string;
    jobTitle: string;
    jobDescription?: string;
    startDate?: Date;
    endDate?: Date;
    isCurrentJob: boolean;
    location?: string;
    managerName?: string;
    managerContact?: string;
}

// ..\mowazaf\Data\Employee\InputModels\UserEducationInput.cs
export interface UserEducationInput {
    id: number;
    userId: string;
    institutionName?: string;
    degree?: string;
    fieldOfStudy?: string;
    startDate?: Date;
    endDate?: Date;
    grade?: string;
}

// ..\mowazaf\Data\Employee\InputModels\UserWorkExperienceInput.cs
export interface UserWorkExperienceInput {
    id: number;
    userId: string;
    companyName?: string;
    jobTitle?: string;
    jobDescription?: string;
    startDate?: Date;
    endDate?: Date;
    isCurrentJob: boolean;
    location?: string;
    managerName?: string;
    managerContact?: string;
}

// ..\mowazaf\Data\Employee\ViewModels\UserEducationView.cs
export interface UserEducationView {
    id: number;
    userId: string;
    user?: User;
    institutionName: string;
    degree: string;
    fieldOfStudy?: string;
    startDate?: Date;
    endDate?: Date;
    grade?: string;
}

// ..\mowazaf\Data\Employee\ViewModels\UserWorkExperienceView.cs
export interface UserWorkExperienceView {
    id: number;
    userId: string;
    user?: User;
    companyName: string;
    jobTitle: string;
    jobDescription?: string;
    startDate?: Date;
    endDate?: Date;
    isCurrentJob: boolean;
    location?: string;
    managerName?: string;
    managerContact?: string;
}

// ..\mowazaf\Data\Employee\Entities\ProficiencyLevel.cs
export enum ProficiencyLevel {
    Awareness = 'Awareness',
    Working = 'Working',
    Strong = 'Strong'
}

// ..\mowazaf\Data\Employee\ViewModels\UserSkillView.cs
export interface UserSkillView {
    id: number;
    userId: string;
    skillId: number;
    skillName: string;
    proficiency: ProficiencyLevel;
    reportedById?: string;
    reportedByName?: string;
    createdOn: Date;
}

// ..\mowazaf\Data\Employee\InputModels\UserSkillInput.cs
export interface UserSkillInput {
    id: number;
    userId: string;
    skillName: string;
    proficiency: ProficiencyLevel;
}

// ..\mowazaf\Data\Employee\ViewModels\SkillView.cs
export interface SkillView {
    id: number;
    name: string;
    description?: string;
}

// ..\mowazaf\Data\Employee\InputModels\SkillFilterInput.cs
export interface SkillFilterInput {
    searchTerm?: string;
    departmentId?: number;
    proficiency?: ProficiencyLevel;
}

// Proficiency breakdown for inventory view
export interface ProficiencyBreakdown {
    Strong: number;
    Working: number;
    Awareness: number;
}

// ..\mowazaf\Data\Employee\ViewModels\SkillInventoryView.cs
export interface SkillInventoryView {
    skillId: number;
    skillName: string;
    totalEmployees: number;
    byProficiency: ProficiencyBreakdown;
    departments: string[];
}

// ..\mowazaf\Data\Employee\Entities\SkillEvidenceType.cs
export enum SkillEvidenceType {
    SkillAdded = 'SkillAdded',
    Endorsement = 'Endorsement',
    ProficiencyChange = 'ProficiencyChange',
    Feedback = 'Feedback'
}

// ..\mowazaf\Data\Employee\ViewModels\SkillEvidenceView.cs
export interface SkillEvidenceView {
    id: number;
    userSkillId: number;
    evidenceType: SkillEvidenceType;
    title: string;
    description?: string;
    authorId?: string;
    authorName?: string;
    evidenceDate: Date;
}

// ..\mowazaf\Data\Employee\Entities\SocialLinkPlatform.cs
export enum SocialLinkPlatform {
    LinkedIn = 'LinkedIn',
    GitHub = 'GitHub',
    Twitter = 'Twitter',
    Facebook = 'Facebook',
    Instagram = 'Instagram',
    YouTube = 'YouTube',
    Website = 'Website',
    Other = 'Other'
}

// ..\mowazaf\Data\Employee\Entities\UserSocialLink.cs
export interface UserSocialLink extends Record {
    id: number;
    userId: string;
    user?: User;
    platform: SocialLinkPlatform;
    url: string;
    displayName?: string;
}

// ..\mowazaf\Data\Employee\InputModels\UserSocialLinkInput.cs
export interface UserSocialLinkInput {
    id: number;
    userId: string;
    platform: SocialLinkPlatform;
    url: string;
    displayName?: string;
}

// ..\mowazaf\Data\Employee\ViewModels\UserSocialLinkView.cs
export interface UserSocialLinkView {
    id: number;
    userId: string;
    platform: SocialLinkPlatform;
    url: string;
    displayName?: string;
}

// ..\mowazaf\Data\Employee\Entities\AchievementCategory.cs
export enum AchievementCategory {
    Recognition = 'Recognition',
    Collaboration = 'Collaboration',
    Innovation = 'Innovation',
    Excellence = 'Excellence',
    Milestone = 'Milestone',
    Leadership = 'Leadership',
    Other = 'Other'
}

// ..\mowazaf\Data\Employee\Entities\ProfileAchievement.cs
export interface ProfileAchievement {
    id: number;
    userId: string;
    title: string;
    description?: string;
    icon: string;
    category: AchievementCategory;
    earnedDate: Date;
    isPublic: boolean;
}

// ..\mowazaf\Data\Employee\InputModels\ProfileAchievementInput.cs
export interface ProfileAchievementInput {
    id: number;
    userId: string;
    title: string;
    description?: string;
    icon: string;
    category: AchievementCategory;
    earnedDate?: Date;
    isPublic: boolean;
}

// ..\mowazaf\Data\Employee\ViewModels\ProfileAchievementView.cs
export interface ProfileAchievementView {
    id: number;
    userId: string;
    title: string;
    description?: string;
    icon: string;
    category: AchievementCategory;
    earnedDate: Date;
    isPublic: boolean;
}

// ── Employment Lifecycle Settings ───────────────────────────────

// ..\mowazaf\Data\Employee\ViewModels\MovementTypeView.cs
export interface MovementTypeView {
    id: number;
    name: string;
    isActive: boolean;
    companyId: number;
}

// ..\mowazaf\Data\Employee\InputModels\MovementTypeInput.cs
export interface MovementTypeInput {
    id?: number;
    name: string;
    isActive: boolean;
}

// ..\mowazaf\Data\Employee\ViewModels\ExitReasonView.cs
export interface ExitReasonView {
    id: number;
    name: string;
    isActive: boolean;
    // Voluntary resignation (true) vs involuntary termination (false). Null/undefined = unclassified (name-heuristic fallback).
    isResignation?: boolean | null;
    companyId: number;
}

// ..\mowazaf\Data\Employee\InputModels\ExitReasonInput.cs
export interface ExitReasonInput {
    id?: number;
    name: string;
    isActive: boolean;
    isResignation?: boolean | null;
}

// ..\mowazaf\Data\Employee\ViewModels\OnboardingTaskTypeView.cs
export interface OnboardingTaskTypeView {
    id: number;
    name: string;
    isActive: boolean;
    companyId: number;
}

// ..\mowazaf\Data\Employee\InputModels\OnboardingTaskTypeInput.cs
export interface OnboardingTaskTypeInput {
    id?: number;
    name: string;
    isActive: boolean;
}

// ..\mowazaf\Data\Employee\ViewModels\OffboardingTaskTypeView.cs
export interface OffboardingTaskTypeView {
    id: number;
    name: string;
    isActive: boolean;
    companyId: number;
}

// ..\mowazaf\Data\Employee\InputModels\OffboardingTaskTypeInput.cs
export interface OffboardingTaskTypeInput {
    id?: number;
    name: string;
    isActive: boolean;
}

// ..\mowazaf\Data\Employee\ViewModels\MovementTaskTypeView.cs
export interface MovementTaskTypeView {
    id: number;
    name: string;
    isActive: boolean;
    companyId: number;
}

// ..\mowazaf\Data\Employee\InputModels\MovementTaskTypeInput.cs
export interface MovementTaskTypeInput {
    id?: number;
    name: string;
    isActive: boolean;
}

// ── Employment Lifecycle Templates ──────────────────────────────

export enum TemplateCategory {
    Onboarding = 0,
    Offboarding = 1,
    Movement = 2,
}

export interface LifecycleTemplateItemView {
    id: number;
    templateId: number;
    name: string;
    typeId?: number;
    typeCategory?: string;
    typeName?: string;
    mandatory: boolean;
    isBlocking: boolean;
    isPolicyAcceptanceTask: boolean;
    defaultOwner: string;
    dueDateOffset: number;
    sortOrder: number;
}

export interface LifecycleTemplateView {
    id: number;
    name: string;
    description?: string;
    isDefault: boolean;
    templateCategory: TemplateCategory;
    companyId: number;
    items: LifecycleTemplateItemView[];
}

export interface LifecycleTemplateItemInput {
    name: string;
    typeId?: number;
    mandatory: boolean;
    isBlocking: boolean;
    isPolicyAcceptanceTask: boolean;
    defaultOwner: string;
    dueDateOffset: number;
    sortOrder: number;
}

export interface LifecycleTemplateInput {
    id?: number;
    name: string;
    description?: string;
    isDefault: boolean;
    templateCategory: TemplateCategory;
    items: LifecycleTemplateItemInput[];
}

export interface DuplicateTemplateInput {
    newName: string;
}

// ── Employment Lifecycle - Onboarding Processes ─────────────────

export enum OnboardingStatus {
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
}

export enum ProcessTaskStatus {
    Pending = 'Pending',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
}

export enum EmploymentStatus {
    Active = 'Active',
    OnProbation = 'OnProbation',
    Onboarding = 'Onboarding',
    Offboarding = 'Offboarding',
    Terminated = 'Terminated',
    Resigned = 'Resigned',
}

export interface OnboardingProcessTaskView {
    id: number;
    processId: number;
    name: string;
    typeId?: number;
    typeName?: string;
    status: ProcessTaskStatus;
    dueDate?: string;
    mandatory: boolean;
    isBlocking: boolean;
    isPolicyAcceptanceTask: boolean;
    assigneeId?: string;
    assigneeName?: string;
    sortOrder: number;
}

export interface OnboardingProcessView {
    id: number;
    userId: string;
    employeeName?: string;
    employeeUniqueId?: string;
    templateId?: number;
    templateName?: string;
    startDate: string;
    managerId: string;
    managerName?: string;
    position: string;
    department: string;
    status: OnboardingStatus;
    completionPercentage: number;
    companyId: number;
    tasks: OnboardingProcessTaskView[];
}

export interface OnboardingProcessInput {
    userId: string;
    templateId?: number;
    startDate: string;
    managerId: string;
}

export interface OnboardingProcessTaskInput {
    name: string;
    typeId?: number;
    mandatory: boolean;
    isBlocking: boolean;
    isPolicyAcceptanceTask: boolean;
    dueDate?: string;
    assigneeId?: string;
    sortOrder: number;
}

export interface MyBlockingStatusView {
    isBlocked: boolean;
    termsPending: boolean;
    currentTermsVersion?: number;
    onboardingProcessId?: number;
    blockingTasks: OnboardingProcessTaskView[];
    nonBlockingTasks: OnboardingProcessTaskView[];
}

export interface DashboardOnboardingTaskView {
    id: number;
    processId: number;
    name: string;
    status: ProcessTaskStatus;
    dueDate?: string;
    mandatory: boolean;
    isBlocking: boolean;
    isPolicyAcceptanceTask: boolean;
    assigneeId?: string;
    assigneeName?: string;
    employeeId: string;
    employeeName?: string;
    /** "Onboarding" | "Offboarding" | "Movement" */
    lifecycleType: string;
}

export interface UpdateTaskStatusInput {
    status: ProcessTaskStatus;
}

export interface EmployeeSelectionView {
    id: string;
    fullName: string;
    title?: string;
    department?: string;
    employeeUniqueId?: string;
}

// ── Employment Lifecycle - Offboarding Processes ─────────────────

export enum OffboardingStatus {
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
}

export interface OffboardingProcessTaskView {
    id: number;
    processId: number;
    name: string;
    typeId?: number;
    typeName?: string;
    status: ProcessTaskStatus;
    dueDate?: string;
    mandatory: boolean;
    isBlocking: boolean;
    assigneeId?: string;
    assigneeName?: string;
    sortOrder: number;
}

export interface OffboardingProcessView {
    id: number;
    userId: string;
    employeeName?: string;
    employeeUniqueId?: string;
    templateId?: number;
    templateName?: string;
    lastWorkingDay: string;
    exitReasonId: number;
    exitReasonName?: string;
    managerId: string;
    managerName?: string;
    position: string;
    department: string;
    status: OffboardingStatus;
    completionPercentage: number;
    eosAmount?: number;
    currencyCode?: string;
    companyId: number;
    tasks: OffboardingProcessTaskView[];
}

export interface OffboardingProcessInput {
    userId: string;
    templateId?: number;
    lastWorkingDay: string;
    exitReasonId: number;
    managerId: string;
}

// Fast-terminate (no offboarding checklist) — creates no OffboardingProcess row.
export interface TerminateDirectInput {
    userId: string;
    exitReasonId?: number;
    lastWorkingDay?: string;
    rehireEligible?: boolean;
}

export interface TerminateDirectResultView {
    userId: string;
    employmentStatus: string;
    lastWorkingDay?: string;
    terminationDate?: string;
    eosCalculated: boolean;
    eosAmount?: number;
    note?: string;
}

// Reverse a completed termination/resignation (A4). Reason is mandatory + audit-logged; a Paid
// settlement is only reversed when allowPaidSettlementReversal is set.
export interface ReverseTerminationInput {
    userId: string;
    reason: string;
    allowPaidSettlementReversal?: boolean;
}

export interface ReverseTerminationResultView {
    userId: string;
    employmentStatus: string;
    rehireEligible: boolean;
    shape: string;
    endOfServiceArchived: boolean;
    settlementArchived: boolean;
    paidSettlementReversed: boolean;
    note?: string;
}

export interface OffboardingProcessTaskInput {
    name: string;
    typeId?: number;
    mandatory: boolean;
    isBlocking: boolean;
    dueDate?: string;
    assigneeId?: string;
    sortOrder: number;
}

// ── Employment Lifecycle - Internal Movements ─────────────────

export enum ApprovalStatus {
    PendingManagerApproval = 'PendingManagerApproval',
    PendingHrApproval = 'PendingHrApproval',
    Approved = 'Approved',
    Rejected = 'Rejected',
}

export enum MovementStatus {
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
}

export interface MovementTaskView {
    id: number;
    movementId: number;
    name: string;
    typeId?: number;
    typeName?: string;
    status: ProcessTaskStatus;
    dueDate?: string;
    mandatory: boolean;
    isBlocking: boolean;
    assigneeId?: string;
    assigneeName?: string;
    sortOrder: number;
}

export interface InternalMovementView {
    id: number;
    userId: string;
    employeeName?: string;
    employeeUniqueId?: string;
    movementTypeId: number;
    movementTypeName?: string;
    templateId?: number;
    templateName?: string;
    currentPosition: string;
    newPosition: string;
    currentDepartmentId?: number;
    currentDepartmentName?: string;
    newDepartmentId?: number;
    newDepartmentName?: string;
    effectiveDate: string;
    reason?: string;
    approvalStatus: ApprovalStatus;
    managerApprovedById?: string;
    managerApprovedByName?: string;
    managerApprovalDate?: string;
    hrApprovedById?: string;
    hrApprovedByName?: string;
    hrApprovalDate?: string;
    rejectedById?: string;
    rejectedByName?: string;
    rejectionReason?: string;
    status: MovementStatus;
    completionPercentage: number;
    companyId: number;
    tasks: MovementTaskView[];
}

export interface InternalMovementInput {
    userId: string;
    movementTypeId: number;
    templateId?: number;
    newPosition: string;
    newDepartmentId?: number;
    effectiveDate: string;
    reason?: string;
}

export interface InternalMovementUpdateInput {
    movementTypeId: number;
    newPosition: string;
    newDepartmentId?: number;
    effectiveDate: string;
    reason?: string;
}

export interface MovementTaskInput {
    name: string;
    typeId?: number;
    mandatory: boolean;
    isBlocking: boolean;
    dueDate?: string;
    assigneeId?: string;
    sortOrder: number;
}

export interface RejectMovementInput {
    rejectionReason: string;
}

export interface EmployeeMovementHistoryView {
    id: number;
    userId: string;
    employeeName?: string;
    movementId: number;
    changeType: string;
    fieldName: string;
    oldValue?: string;
    newValue?: string;
    effectiveDate: string;
}

export interface DepartmentSelectionView {
    id: number;
    name: string;
}

// ..\mowazaf\Data\Employee\Entities\EmployeeMovement.cs
export enum MovementType {
    Promotion = 'Promotion',
    Transfer = 'Transfer',
    Demotion = 'Demotion',
    LateralMove = 'LateralMove',
    SalaryAdjustment = 'SalaryAdjustment',
    ContractRenewal = 'ContractRenewal'
}

export enum MovementApprovalStatus {
    Pending = 'Pending',
    Approved = 'Approved',
    Rejected = 'Rejected',
    Cancelled = 'Cancelled'
}

export interface EmployeeMovementInput {
    userId: string;
    movementType: MovementType;
    newTitle?: string;
    newDepartmentId?: number;
    newBranchId?: number;
    newSalary?: number;
    effectiveDate: string;
    reason?: string;
    notes?: string;
}

export interface EmployeeMovementView extends Record {
    id: number;
    userId: string;
    userName?: string;
    companyId: number;
    movementType: MovementType;
    previousTitle?: string;
    previousDepartmentId?: number;
    previousDepartmentName?: string;
    previousBranchId?: number;
    previousBranchName?: string;
    previousSalary?: number;
    newTitle?: string;
    newDepartmentId?: number;
    newDepartmentName?: string;
    newBranchId?: number;
    newBranchName?: string;
    newSalary?: number;
    effectiveDate: string;
    reason?: string;
    approvalStatus: MovementApprovalStatus;
    approvedById?: string;
    approvedByName?: string;
    approvedOn?: string;
    notes?: string;
}
